import {
  Component,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  inject
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
  MatDialogContent,
  MatDialogActions
} from '@angular/material/dialog';
import {
  PropertyImage,
  CreatePropertyImageRequest
} from '../../../models/property.model';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormArray, FormGroup } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatStepperModule } from '@angular/material/stepper';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { SnackBarService } from '../../../../../shared/services/snackbar.service';
import { PropertyService } from '../../../services/property.service';
import { lastValueFrom } from 'rxjs';

interface NewImage {
  file: File;
  preview: string;
  isNew: true;
}

@Component({
  selector: 'app-images-edit',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatCardModule,
    MatStepperModule,
    MatDatepickerModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    DragDropModule
  ],
  templateUrl: './images-edit.html',
  styleUrl: './images-edit.scss'
})
export class ImagesEdit implements OnInit, OnDestroy {
  private snackbar = inject(SnackBarService);
  private propertyService = inject(PropertyService);

  // ────── Injections ──────
  private dialogRef = inject(MatDialogRef<ImagesEdit>);
  private cdr = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  readonly data = inject<{ propertyImages: PropertyImage[], propertyId: string }>(MAT_DIALOG_DATA);

  // ────── Form ──────
  form: FormGroup;

  // ────── Single source of truth ──────
  allImages: (PropertyImage | NewImage)[] = [];

  // ────── UI state ──────
  maxImages = 15;
  isProcessing = false;
  isProcessingImages = false;
  processedImages = 0;
  totalImages = 0;

  constructor() {
    this.form = this.fb.group({
      images: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.loadExistingImages();
    this.syncImagesFormArray();
  }

  ngOnDestroy(): void {
    this.cleanupBlobUrls();
  }

  /* ────── Load existing images (normalize url → imageUrl) ────── */
  private loadExistingImages(): void {
    if (!this.data.propertyImages || this.data.propertyImages.length === 0) return;

    const valid = this.data.propertyImages
      .filter(img => img && ((img as any).imageUrl || (img as any).url))
      .sort((a, b) => (a.sortOrder ?? a.sortOrder ?? 0) - (b.sortOrder ?? b.sortOrder ?? 0));

    this.allImages = valid.map(img => {
      const { isNew, ...clean } = img as any;
      return {
        ...clean,
        imageUrl: clean.imageUrl || clean.url || '',
        isNew: false as const
      };
    });
  }

  /* ────── File handling ────── */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files) this.handleFiles(files);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onImagesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) this.handleFiles(input.files);
  }

  private handleFiles(fileList: FileList): void {
    if (this.allImages.length >= this.maxImages) {
      alert(`Maximum ${this.maxImages} images allowed.`);
      return;
    }
    for (let i = 0; i < fileList.length; i++) {
      if (this.allImages.length >= this.maxImages) break;
      const file = fileList[i];
      if (this.isValidImage(file)) {
        const preview = URL.createObjectURL(file);
        this.allImages.push({ file, preview, isNew: true });
      }
    }
    this.syncImagesFormArray();
    this.cdr.detectChanges();
  }

  private isValidImage(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 10 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      alert('Only JPG, PNG, WebP allowed!');
      return false;
    }
    if (file.size > maxSize) {
      alert('Max 10 MB per image!');
      return false;
    }
    return true;
  }

  /* ────── Remove ────── */
  removeImage(index: number): void {
    const item = this.allImages[index];
    if ((item as any).isNew === true) {
      const newImg = item as NewImage;
      if (newImg.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(newImg.preview);
      }
    }
    this.allImages.splice(index, 1);
    this.syncImagesFormArray();
    this.cdr.detectChanges();
  }

  /* ────── Reorder ────── */
  onDropImage(event: CdkDragDrop<(PropertyImage | NewImage)[]>): void {
    moveItemInArray(this.allImages, event.previousIndex, event.currentIndex);
    this.syncImagesFormArray();
    this.cdr.detectChanges();
  }

  /* ────── FormArray sync ────── */
  private syncImagesFormArray(): void {
    const array = this.imagesArray;
    while (array.length !== 0) array.removeAt(0);
    this.allImages.forEach(() => array.push(this.fb.control(null)));
    array.markAsTouched();
  }

  get imagesArray(): FormArray {
    return this.form.get('images') as FormArray;
  }

  /* ────── Base64 conversion (inside save) ────── */
  private async convertNewImages(): Promise<CreatePropertyImageRequest[]> {
    const newImages = this.allImages.filter((img): img is NewImage => (img as any).isNew === true);
    if (newImages.length === 0) return [];

    this.isProcessingImages = true;
    this.processedImages = 0;
    this.totalImages = newImages.length;
    this.cdr.detectChanges();

    const requests: CreatePropertyImageRequest[] = [];
    for (let i = 0; i < newImages.length; i++) {
      const file = newImages[i].file;
      if (!file) continue;
      try {
        const base64 = await this.fileToBase64(file);
        const currentIndex = this.allImages.indexOf(newImages[i]);
        requests.push({
          file: base64,
          contentType: file.type,
          description: '',
          sortOrder: currentIndex
        });
        this.processedImages = i + 1;
        this.cdr.detectChanges();
      } catch (e) {
        console.error('Base64 error:', e);
      }
    }

    this.isProcessingImages = false;
    this.cdr.detectChanges();
    return requests;
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /* ────── Save (robust, no missing request) ────── */
  async save(): Promise<void> {
    if (this.allImages.length === 0) {
      this.snackbar.open('Please add at least one image.', ' ', {
        duration: 5000,
        panelClass: ['error-snackbar'],
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
      return;
    }
    this.isProcessing = true;
    try {
      // 1. Identify which images are actually new
      const newItems = this.allImages.filter(img => (img as any).isNew === true) as NewImage[];
      
      // 2. Upload them ONE BY ONE to get their IDs
      // We store them in a Map using their temporary 'preview' (blob URL) as a key
      const idMap = new Map<string, PropertyImage>();

      for (const item of newItems) {
        const base64 = await this.fileToBase64(item.file);
        const savedImage = await lastValueFrom(
          this.propertyService.createPropertyImage(this.data.propertyId, {
            file: base64,
            contentType: item.file.type,
            description: '',
            sortOrder: 0
          })
        );
        // Map the blob preview URL to the newly created server object
        idMap.set(item.preview, savedImage);
      }

      // 3. Build the FINAL payload for the PUT endpoint
      // This payload handles UPDATES, REORDERING, and DELETIONS
      const finalPayload = this.allImages.map((img, index) => {
        let source: PropertyImage;

        if ((img as any).isNew) {
          // Retrieve the version with the ID we just got from the server
          source = idMap.get((img as NewImage).preview)!;
        } else {
          source = img as PropertyImage;
        }

        // Explicitly whitelist fields to avoid the "order" error
        return {
          id: source.id,
          imageUrl: source.imageUrl,
          description: source.description || '',
          sortOrder: index, // Backend uses this to set sequence
          createdAt: source.createdAt,
          updatedAt: source.updatedAt
        };
      });

      console.log('Sending to PUT Sync:', finalPayload);

      // 4. Final Sync
      // This call tells the backend: "Keep these, update their order, and delete the rest"
      const result = await lastValueFrom(
        this.propertyService.updatePropertyImages(this.data.propertyId, finalPayload)
      );

      this.snackbar.open('Image updates saved', ' ', {
        duration: 5000,
        panelClass: ['success-snackbar'],
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
      this.dialogRef.close(result);

    } catch (err) {
      console.error('Save failed:', err);
      this.snackbar.open('Failed to save images', ' ', {
        duration: 5000,
        panelClass: ['error-snackbar'],
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  private isExistingImage(img: PropertyImage | NewImage): img is PropertyImage {
    return (img as any).isNew !== true;
  }

  /* ────── Cancel ────── */
  cancel(): void {
    this.cleanupBlobUrls();
    this.dialogRef.close();
  }

  private cleanupBlobUrls(): void {
    this.allImages.forEach(item => {
      if ((item as any).isNew === true) {
        const newImg = item as NewImage;
        if (newImg.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(newImg.preview);
        }
      }
    });
  }

  /* ────── Preview helper (public) ────── */
  getPreview(img: PropertyImage | NewImage): string {
    if (!img) return '';

    // Only check preview if it's a NEW image
    if ((img as any).isNew === true) {
      const newImg = img as NewImage;
      return newImg.preview || '';
    }

    const propImg = img as PropertyImage;
    return propImg.imageUrl || (img as any).url || '';
  }
}